import { usePricingContentContext } from '@/components/wpl/pricing/src/pricing-content-context.tsx';
import { PricingFaq, FaqSectionProps } from '@/components/wpl/pricing/src/pricing-faq/pricing-faq';
import { PricingPageLayout } from '@/components/wpl/pricing/src/pricing-page-layout/pricing-page-layout';
import { LoomPriceFinder } from '@/templates/software/loom/pricing/sections/loom-price-finder.tsx';
import { ParentScrollTracking } from '@/utils/scroll-tracking/parent-scroll-tracker';
import { UserLocale } from '@/utils/useGeoCurrency.ts';

import { PricingTabs } from '../../../components/wpl/pricing/src/tabs.tsx';
import { AppPricingData } from '../../../contentful/product-catalog';
import { ConfluencePriceFinder } from '../src/sections/confluence-price-finder.tsx';
import { JiraPriceFinder } from '../src/sections/jira-price-finder.tsx';

import { getIsGwp10027NonprofitPricingPage } from './feature-gates.ts';
import { NonProfitPricingPageTemplateWithTwc } from './non-profit-pricing-page-with-twc.template.tsx';
import { Banner } from './sections/banner.tsx';
import { EligibleSection } from './sections/eligible-section.tsx';
import { EnterpriseSection } from './sections/enterprise-section.tsx';
import { Fineprint } from './sections/fineprint.tsx';
import { Hero } from './sections/hero.tsx';
import { NonprofitCalculatorHeading } from './sections/nonprofit-calculator-heading.tsx';
import { NonprofitNav } from './sections/nonprofit-nav.tsx';
import { OfficialSection } from './sections/official-section.tsx';
import { ResourcesSection } from './sections/resources-section.tsx';
import { TrelloPriceFinder } from './sections/trello-price-finder.tsx';

export interface NonProfitPricingPageTemplateProps {
	pricing?: AppPricingData;
	countryCode?: UserLocale;
}

export const NonProfitPricingPageTemplate = ({
	pricing,
	countryCode,
}: NonProfitPricingPageTemplateProps) => {
	const content = usePricingContentContext();

	const isGwp10027NonprofitPricingPage = getIsGwp10027NonprofitPricingPage();
	if (isGwp10027NonprofitPricingPage) {
		return <NonProfitPricingPageTemplateWithTwc />;
	}

	return (
		<>
			<ParentScrollTracking pageId="nonprofit-pricing-page">
				<PricingPageLayout
					nav={<NonprofitNav />}
					banner={<Banner />}
					hero={<Hero />}
					hasGlobalFooter={false}
				/>
				<PricingPageLayout bgColor="#F8F8F8" hasGlobalFooter={false}>
					<ParentScrollTracking>
						<EligibleSection />
					</ParentScrollTracking>
				</PricingPageLayout>
				<PricingPageLayout hasGlobalFooter={false}>
					<ParentScrollTracking>
						<NonprofitCalculatorHeading />
						<PricingTabs
							jiraCalculatorComponent={
								<JiraPriceFinder
									pricing={pricing}
									countryCode={countryCode}
									shouldAddDiscount={true}
								/>
							}
							confluenceCalculatorComponent={
								<ConfluencePriceFinder
									pricing={pricing}
									countryCode={countryCode}
									shouldAddDiscount={true}
								/>
							}
							trelloCalculatorComponent={
								<TrelloPriceFinder
									pricing={pricing}
									countryCode={countryCode}
									shouldAddDiscount={true}
								/>
							}
							loomCalculatorComponent={
								<LoomPriceFinder
									pricing={pricing}
									countryCode={countryCode}
									shouldAddDiscount={true}
									ctaUrlSource="productPlans"
								/>
							}
						/>
						<EnterpriseSection />
					</ParentScrollTracking>
				</PricingPageLayout>
				<PricingPageLayout bgColor="#E9F2FE" hasGlobalFooter={false}>
					<ParentScrollTracking>
						<OfficialSection />
					</ParentScrollTracking>
				</PricingPageLayout>
				<PricingPageLayout hasGlobalFooter={false}>
					<ParentScrollTracking>
						<ResourcesSection />
					</ParentScrollTracking>
				</PricingPageLayout>
				<PricingPageLayout hasGlobalFooter={true}>
					<ParentScrollTracking>
						<PricingFaq
							header={content?.getMoreDetailsTitle as FaqSectionProps['header']}
							planDetails={content?.additionalDetails as FaqSectionProps['planDetails']}
						/>
						<Fineprint />
					</ParentScrollTracking>
				</PricingPageLayout>
			</ParentScrollTracking>
		</>
	);
};
